import { useMutation, useQueryClient } from '@tanstack/react-query';

import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { queryKeysUser } from '../queryKeys';
import { type UserIdentityProviderLinksAdmin } from '../types';

import { deleteUserIdentityProviderLinkAdmin } from './deleteUserIdentityProviderLinkAdmin';
import {
  type DeleteUserIdentityProviderLinkAdminMutationOptions,
  type DeleteUserIdentityProviderLinkAdminVariables,
} from './types';

/**
 * Custom hook for removing the single sign-on link of a user's login as ADMIN.
 * A successful removal replaces the user's cached links with the ones that remain. A failed
 * one refetches them instead, and the mutation stays pending until that refetch has settled.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-User/operation/remove_identity_provider_link_vadmin_users__user_id__identity_provider_links__identity_provider__delete
 */
export const useMutationDeleteUserIdentityProviderLinkAdmin = (
  options?: DeleteUserIdentityProviderLinkAdminMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      identityProvider,
      userId,
    }: DeleteUserIdentityProviderLinkAdminVariables) =>
      deleteUserIdentityProviderLinkAdmin(userId, identityProvider),
    ...options,
    async onSettled(data, error, variables, onMutateResult, context) {
      if (error) {
        await queryClient.invalidateQueries({
          queryKey: queryKeysUser.getIdentityProviderLinksAdmin(
            variables.userId,
          ),
        });
      }

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
    onSuccess(data, variables, onMutateResult, context) {
      queryClient.setQueryData<UserIdentityProviderLinksAdmin>(
        queryKeysUser.getIdentityProviderLinksAdmin(variables.userId),
        data,
      );
      ToastService.success(
        i18n.t(($) => $.user.deleteUserIdentityProviderLinkAdmin.success, {
          ns: 'data',
        }),
      );

      return options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
